import { useMemo } from "react"
import * as Yup from "yup"
import { isAddress } from "ethers/lib/utils.js"
import { BigNumber } from "ethers"

import { ExecutableCallType } from "query/graphql"
import { VotingOptionStrategy } from "createProposal/types/form"
import { CREATE_PROPOSAL_ERRORS } from "errors/createProposal"

const { title, description } = CREATE_PROPOSAL_ERRORS.details
const { target, selectedAbiMethod, value, memo } =
  CREATE_PROPOSAL_ERRORS.actions

const MAX_TITLE_CHARS = 80
const MAX_MEMO_CHARS = 140

const titleMaxLengthError = title.maxLengthError(MAX_TITLE_CHARS)
const titleRequiredError = title.required()
const memoMaxLengthError = memo.maxLengthError(MAX_MEMO_CHARS)
const descriptionRequiredError = description.required()
const selectedAbiMethodInputError = selectedAbiMethod.requiredInputs()

const targetAddressRequiredError = target.required()

const valuePositiveError = value.positive()
const valueRequiredError = value.required()
const valueTypeError = value.type()

// FIXME: add Yup type but works bad
export const useValidationSchema = (): any => {
  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      details: Yup.object().shape({
        title: Yup.string()
          .trim()
          .required(titleRequiredError.displayValue)
          .test(
            titleMaxLengthError.name,
            titleMaxLengthError.displayValue,
            (value) => {
              if (!value) return false

              return value.length <= MAX_TITLE_CHARS
            },
          ),
        description: Yup.string().required(
          descriptionRequiredError.displayValue,
        ),
        snapshotURL: Yup.string().trim(),
        discourseURL: Yup.string().trim(),
      }),
      actions: Yup.array().of(
        Yup.object().shape({
          originalExecutableAction: Yup.object()
            .nullable()
            .when("$originalExecutableAction", {
              is: (value: any) => value !== null && typeof value === "object",
              then: Yup.object().required(),
              otherwise: Yup.object().notRequired(),
            }),

          selectedAbiMethod: Yup.object()
            .optional()
            .when("$originalExecutableAction", {
              is: (value: any) => {
                if (value === undefined) return false

                return true
              },
              then: Yup.object()
                .optional()
                .test((selectedAbiMethod, { parent }) => {
                  if (parent.recipe !== ExecutableCallType.Custom) return true

                  if (
                    !selectedAbiMethod.name &&
                    !parent.isTargetAddressExternal
                  ) {
                    return false
                  }

                  return true
                })
                .shape({
                  inputs: Yup.array().of(
                    Yup.object().shape({
                      value: Yup.string().required(
                        selectedAbiMethodInputError.displayValue,
                      ),
                    }),
                  ),
                }),
              otherwise: Yup.object().optional(),
            }),

          targetAddress: Yup.string().when("$originalExecutableAction", {
            is: (value: any) => {
              if (value === undefined) return false

              return true
            },
            then: Yup.string().test(
              targetAddressRequiredError.name,
              targetAddressRequiredError.displayValue,
              (value, { parent }) => {
                if (parent.recipe === ExecutableCallType.Swap) return true

                if (parent.recipe === ExecutableCallType.Custom) return true

                return Boolean(value)
              },
            ),
            otherwise: Yup.string().notRequired(),
          }),

          changedMembers: Yup.string().when("$originalExecutableAction", {
            is: (value: any) => {
              if (value === undefined) return false

              return true
            },
            then: Yup.number().test((value, { parent }) => {
              if (parent.recipe !== ExecutableCallType.Orcamanagepod)
                return true
              if (value) return value > 0

              return false
            }),
            otherwise: Yup.string().notRequired(),
          }),

          value: Yup.string().when("$originalExecutableAction", {
            is: (value: any) => {
              if (value === undefined) return false

              return true
            },
            then: Yup.number()
              .min(0, valuePositiveError.displayValue)
              .required(valueRequiredError.displayValue)
              .typeError(valueTypeError.displayValue),
            otherwise: Yup.string().notRequired(),
          }),

          memo: Yup.string().when("$originalExecutableAction", {
            is: (value: any) => {
              if (value === undefined) return false

              return true
            },
            then: Yup.string().test(
              memoMaxLengthError.name,
              memoMaxLengthError.displayValue,
              (value) => {
                if (!value) return true

                return value.length <= MAX_MEMO_CHARS
              },
            ),
            otherwise: Yup.string().notRequired(),
          }),
          amountIn: Yup.string()
            .optional()
            .test((value, { parent }) => {
              const { recipe } = parent

              if (recipe !== ExecutableCallType.Swap) return true

              try {
                const bigValue = BigNumber.from(value)

                return bigValue.gt(0)
              } catch {
                return false
              }
            }),
          buyToken: Yup.object().test((value, { parent }) => {
            const { recipe } = parent

            if (recipe !== ExecutableCallType.Swap) return true

            return Boolean(value)
          }),
          sellToken: Yup.object().test((value, { parent }) => {
            const { recipe } = parent

            if (recipe !== ExecutableCallType.Swap) return true

            return Boolean(value)
          }),
          priceCheckerAddress: Yup.string()
            .optional()
            .test((value, { parent }) => {
              const { recipe } = parent

              if (recipe !== ExecutableCallType.Swap) return true

              return Boolean(value && isAddress(value))
            }),
        }),
      ),
      votingOption: Yup.object().shape({
        currentChoice: Yup.string().min(1),
        supports: Yup.array()
          .of(Yup.string())
          .test((value, { parent }) => {
            if (parent.strategy === VotingOptionStrategy.DEFAULT) {
              return true
            }

            return Boolean(value && value.length > 1)
          }),
      }),
    })
  }, [])

  return validationSchema
}
